import React, { useState } from 'react';
import { leadsAPI } from '../../services/api';

function AddLead() {
  const currentUserId = localStorage.getItem('id');
  const [newLead, setNewLead] = useState({
    schoolName: '',
    personName: '',
    designation: '',
    address: '',
    phoneNumber: '',
    email: '',
    progressStatus: 'Prospecting',
    statusDescription: '',
    source: 'Website',
    expectedValue: 0,
    progressPlannedEndDate: '',
    startDate: '',
    endDate: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const LeadSourceOptions = [
    { name: 'Website', value: 'Website' },
    { name: 'Event', value: 'Event' },
    { name: 'Referral', value: 'Referral' },
    { name: 'Direct Outreach', value: 'Direct Outreach' },
    { name: 'Marketing Campaign', value: 'Marketing Campaign' },
  ];

  const LeadStatusOptions = [
    { name: 'Cold Lead', value: 'Cold Lead' },
    { name: 'Prospecting', value: 'Prospecting' },
    { name: 'Qualified', value: 'Qualified' },
    { name: 'Demo Scheduled / Done', value: 'Demo Scheduled / Done' },
    { name: 'Evaluation / Pilot', value: 'Evaluation / Pilot' },
    { name: 'Decision', value: 'Decision' },
    { name: 'Closed Won', value: 'Closed Won' },
    { name: 'Closed Lost', value: 'Closed Lost' },
  ];

  const createLead = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Automatically set current user as primary assignee
      const leadData = {
        ...newLead,
        primaryAssignee: currentUserId
      };
      await leadsAPI.createLead(leadData);
      setNewLead({
        schoolName: '',
        personName: '',
        designation: '',
        address: '',
        phoneNumber: '',
        email: '',
        progressStatus: 'Prospecting',
        statusDescription: '',
        source: 'Website',
        expectedValue: 0,
        progressPlannedEndDate: '',
        startDate: '',
        endDate: '',
      });
      setSuccess('Lead created successfully! It will appear in the lead listing.');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create lead');
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-6">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-blue-600 bg-clip-text text-transparent">
              Add New Lead
            </h2>
            <p className="text-gray-600 mt-1">Create new leads for the system (you will be automatically assigned as primary assignee)</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6">
            {success}
          </div>
        )}

        <form onSubmit={createLead} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School Name *</label>
              <input
                type="text"
                placeholder="Enter school name"
                value={newLead.schoolName}
                onChange={(e) => setNewLead({ ...newLead, schoolName: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person Name</label>
              <input
                type="text"
                placeholder="Enter contact person name"
                value={newLead.personName}
                onChange={(e) => setNewLead({ ...newLead, personName: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
              <input
                type="text"
                placeholder="Enter designation"
                value={newLead.designation}
                onChange={(e) => setNewLead({ ...newLead, designation: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <input
                type="email"
                placeholder="Enter email address"
                value={newLead.email}
                onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
              <input
                type="tel"
                placeholder="Enter phone number"
                value={newLead.phoneNumber}
                onChange={(e) => setNewLead({ ...newLead, phoneNumber: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
              <input
                type="text"
                placeholder="Enter address"
                value={newLead.address}
                onChange={(e) => setNewLead({ ...newLead, address: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lead Source</label>
              <select
                value={newLead.source}
                onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {LeadSourceOptions.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Progress Status</label>
              <select
                value={newLead.progressStatus}
                onChange={(e) => setNewLead({ ...newLead, progressStatus: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {LeadStatusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status Description</label>
              <input
                type="text"
                placeholder="Optional description for current status"
                value={newLead.statusDescription}
                onChange={(e) => setNewLead({ ...newLead, statusDescription: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expected Value</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter expected value"
                value={newLead.expectedValue}
                onChange={(e) => setNewLead({ ...newLead, expectedValue: parseFloat(e.target.value) || 0 })}
                className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium shadow-lg disabled:opacity-50"
            >
              {loading ? 'Creating Lead...' : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddLead;